import React, { useEffect, useLayoutEffect, useState } from "react";
import Chart, { loaderTypes } from "../../../Components/Chart/Chart";
import useChart from "../../../Components/Chart/useChart";
import { useDebounce, useMeasure } from "react-use";
import * as am4charts from "@amcharts/amcharts4/charts";
import { startCase, toLower } from "lodash-es";
import { TICKET_SLA } from "../SLA/SLAUtils";
import useWidgetFilterNavigator from "../../useWidgetFilterNavigator";
import {
  buildChartData,
  createCategoryAxis,
  createSeries,
  createValueAxis,
  type ChartDataPoint,
} from "../TableCharts/WidthSensetiveStackedBarChartUtils";

export interface TicketDueDateProps {
  // Post-`JSON.parse` `per_ticket_due_date_status` map (status key -> count),
  // matching `ScopeSummaryItem["per_ticket_due_date_status"]` from useScopesGraph.
  scopeData?: Record<string, number>;
  max: number;
  scopeId?: string;
  totalCount: number;
  loading: boolean;
  setMax: (max: number) => void;
  widgetId: string;
  filterToExecute?: string;
  isForExport?: boolean;
}

const TicketDueDate: React.FC<TicketDueDateProps> = ({
  scopeData,
  max,
  scopeId,
  totalCount,
  loading,
  setMax,
  widgetId,
  filterToExecute,
  isForExport,
}) => {
  const [dashboardData, setDashboardData] = useState<ChartDataPoint[] | null>(
    null
  );
  const [ref, { width: chartWidth }] = useMeasure<HTMLDivElement>();
  const [debouncedWidth, setDebouncedWidth] = useState(chartWidth);
  const chartName = `TicketDueDateDiv-${scopeId}-${widgetId}`;

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: null,
    scopeId,
  });

  const {
    chartLoading,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(isForExport ? () => {} : null);

  useEffect(() => {
    if (!!loading) {
      startLoadingChart();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [loading]);

  useDebounce(
    () => {
      setDebouncedWidth(chartWidth);
    },
    1000,
    [chartWidth]
  );

  useEffect(() => {
    if (!!scopeData) {
      const { NA, ...scopeDataFiltered } = scopeData;
      const entries = Object.keys(scopeDataFiltered);

      const data = buildChartData(
        entries,
        entries.map((key) => scopeData[key]),
        totalCount,
        max,
        // Original passes `null` when width is falsy; buildChartData guards
        // `if (!!width)`, so null/0 are runtime-valid. Cast preserves behavior.
        (debouncedWidth || null) as number,
        { scopeId: "" }
      );

      const realTotal = entries.reduce(
        (sum, key) => sum + (scopeData[key] || 0),
        0
      );
      if (realTotal > max) {
        setMax(realTotal);
      }

      setDashboardData([data]);
      setChartError(false);
      stopLoadingChart();
      setChartDataExists(true);
    }
  }, [scopeData, debouncedWidth, max]);

  const getChart = (
    dashboardData: ChartDataPoint[]
  ): am4charts.XYChart | undefined => {
    if (dataExists) {
      let [chart] = createChart(chartName, am4charts.XYChart, {
        hideLegend: true,
      });
      chart.data = dashboardData;
      chart.paddingTop = isForExport ? 0 : 16;
      chart.paddingLeft = isForExport ? 0 : 6;
      chart.paddingRight = isForExport ? 0 : 6;

      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      createCategoryAxis(categoryAxis, "scopeId");
      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, max);

      const filteredKeys = Object.keys(dashboardData[0]).filter(
        (el) => el !== "scopeId" && !el.endsWith("Val")
      );

      filteredKeys.forEach((key) => {
        if (!dashboardData[0][key]) {
          return;
        }

        const series = createSeries(
          `${key}Val`,
          startCase(toLower(key)),
          dashboardData[0][key] as number,
          // `key` is a runtime ticket_due_date_status (NA already excluded), so
          // the TICKET_SLA lookup and its `fill` are always defined here.
          TICKET_SLA[key as keyof typeof TICKET_SLA]?.fill as string,
          "scopeId",
          !!isForExport,
          !!onFilterClick
        );

        series.columns.template.events.on("hit", () => {
          onFilterClick([
            ["in", "remediation_item.ticket_due_date_status", key],
          ]);
        });

        chart.series.push(series);
      });

      return chart;
    }
  };

  useLayoutEffect(() => {
    if (!!dashboardData) {
      const chart = getChart(dashboardData);

      return () => {
        chart?.dispose();
      };
    }
  }, [dashboardData, createChart, isForExport]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={false}
      chartData={dataExists}
      hideLegend
      chartDivClassName={"ticketDueDateDiv"}
      loaderType={loaderTypes.SKELETON}
      // useMeasure returns a callback ref; Chart types chartContainerRef as a
      // RefObject. Behavior is unchanged — cast bridges the ref-shape mismatch.
      chartContainerRef={ref as unknown as React.RefObject<HTMLDivElement>}
    />
  );
};

export default TicketDueDate;
